import React, { useState, useEffect } from 'react';
import { disponibilidadAPI } from '../services/api';
import { authAPI } from '../services/api';
import './DisponibilidadConfig.css';

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DURACIONES = [30, 45, 60, 90];

function DisponibilidadConfig() {
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);
  const [semanal, setSemanal] = useState([]);
  const [excepciones, setExcepciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [configForm, setConfigForm] = useState({
    habilitado: false,
    duracion_cita_minutos: 60,
    buffer_minutos: 15,
    titulo_pagina: '',
    mensaje_bienvenida: '',
    mensaje_bienvenida_en: '',
    dias_anticipacion_max: 30,
    dias_anticipacion_min: 1,
  });

  const [nuevaExcepcion, setNuevaExcepcion] = useState({
    fecha: '',
    tipo: 'bloqueado',
    hora_inicio: '',
    hora_fin: '',
    motivo: '',
  });

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadUser = async () => {
    try {
      const res = await authAPI.check();
      if (res.data?.authenticated && res.data?.user) setUser(res.data.user);
    } catch (e) {
      setError('No se pudo cargar el usuario');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await disponibilidadAPI.get();
      const data = res.data;
      setConfig(data.config || {});
      setSemanal(Array.isArray(data.semanal) ? data.semanal : []);
      setExcepciones(Array.isArray(data.excepciones) ? data.excepciones : []);
      setConfigForm({
        habilitado: !!(data.config?.habilitado),
        duracion_cita_minutos: data.config?.duracion_cita_minutos ?? 60,
        buffer_minutos: data.config?.buffer_minutos ?? 15,
        titulo_pagina: data.config?.titulo_pagina ?? '',
        mensaje_bienvenida: data.config?.mensaje_bienvenida ?? '',
        mensaje_bienvenida_en: data.config?.mensaje_bienvenida_en ?? '',
        dias_anticipacion_max: data.config?.dias_anticipacion_max ?? 30,
        dias_anticipacion_min: data.config?.dias_anticipacion_min ?? 1,
      });
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar disponibilidad');
    } finally {
      setLoading(false);
    }
  };

  const getSlotsByDay = (dia) => semanal.filter((s) => Number(s.dia_semana) === dia);

  const addSlot = (dia) => {
    setSemanal((prev) => [...prev, { dia_semana: dia, hora_inicio: '09:00', hora_fin: '10:00', activo: 1 }]);
  };

  const updateSlot = (idx, field, value) => {
    setSemanal((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const removeSlot = (idx) => {
    setSemanal((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveSemanal = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await disponibilidadAPI.saveSemanal(
        semanal.map((s) => ({
          dia_semana: Number(s.dia_semana),
          hora_inicio: s.hora_inicio,
          hora_fin: s.hora_fin,
        }))
      );
      setSuccess('Horario semanal guardado');
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await disponibilidadAPI.updateConfig(configForm);
      setSuccess('Configuración guardada');
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const addExcepcion = async () => {
    const { fecha, tipo, hora_inicio, hora_fin, motivo } = nuevaExcepcion;
    if (!fecha) {
      setError('Indica la fecha');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await disponibilidadAPI.addExcepcion({
        fecha,
        tipo,
        hora_inicio: hora_inicio || null,
        hora_fin: hora_fin || null,
        motivo: motivo || null,
      });
      setNuevaExcepcion({ fecha: '', tipo: 'bloqueado', hora_inicio: '', hora_fin: '', motivo: '' });
      setSuccess('Excepción añadida');
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al añadir excepción');
    } finally {
      setSaving(false);
    }
  };

  const deleteExcepcion = async (id) => {
    try {
      setSaving(true);
      setError(null);
      await disponibilidadAPI.deleteSlot(id, 'excepcion');
      setSuccess('Excepción eliminada');
      loadData();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = user?.username ? `${window.location.origin}/reservar/${encodeURIComponent(user.username)}` : '';

  if (loading && !config) {
    return (
      <div className="disponibilidad-config">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="disponibilidad-config">
      <h1>Disponibilidad y reservas</h1>

      {error && <div className="disp-error">{error}</div>}
      {success && <div className="disp-success">{success}</div>}

      <section className="disp-section">
        <h2>Activar reservas públicas</h2>
        <label className="disp-toggle">
          <input
            type="checkbox"
            checked={!!configForm.habilitado}
            onChange={(e) => setConfigForm((c) => ({ ...c, habilitado: e.target.checked }))}
          />
          Habilitar que los visitantes reserven cita desde el enlace público
        </label>
        <button type="button" className="btn btn-primary" onClick={saveConfig} disabled={saving}>
          Guardar configuración
        </button>
      </section>

      <section className="disp-section">
        <h2>Configuración de la reserva</h2>
        <div className="disp-form-grid">
          <label>
            Duración de la cita (min)
            <select
              value={configForm.duracion_cita_minutos}
              onChange={(e) => setConfigForm((c) => ({ ...c, duracion_cita_minutos: Number(e.target.value) }))}
            >
              {DURACIONES.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </label>
          <label>
            Buffer entre citas (min)
            <input
              type="number"
              min={0}
              value={configForm.buffer_minutos}
              onChange={(e) => setConfigForm((c) => ({ ...c, buffer_minutos: Number(e.target.value) || 0 }))}
            />
          </label>
          <label>
            Reservar con al menos (días)
            <input
              type="number"
              min={0}
              value={configForm.dias_anticipacion_min}
              onChange={(e) => setConfigForm((c) => ({ ...c, dias_anticipacion_min: Number(e.target.value) || 0 }))}
            />
          </label>
          <label>
            Reservar como máximo (días)
            <input
              type="number"
              min={1}
              value={configForm.dias_anticipacion_max}
              onChange={(e) => setConfigForm((c) => ({ ...c, dias_anticipacion_max: Number(e.target.value) || 30 }))}
            />
          </label>
        </div>
        <label className="disp-label-block">
          Título de la página de reserva
          <input
            type="text"
            placeholder="Ej: Reserva una sesión"
            value={configForm.titulo_pagina}
            onChange={(e) => setConfigForm((c) => ({ ...c, titulo_pagina: e.target.value }))}
          />
        </label>
        <label className="disp-label-block">
          Mensaje de bienvenida (ES)
          <textarea
            placeholder="Texto que verán los visitantes al entrar"
            value={configForm.mensaje_bienvenida}
            onChange={(e) => setConfigForm((c) => ({ ...c, mensaje_bienvenida: e.target.value }))}
            rows={3}
          />
        </label>
        <label className="disp-label-block">
          Welcome message (EN)
          <textarea
            placeholder="Text visitors will see (English version)"
            value={configForm.mensaje_bienvenida_en}
            onChange={(e) => setConfigForm((c) => ({ ...c, mensaje_bienvenida_en: e.target.value }))}
            rows={3}
          />
        </label>
        <button type="button" className="btn btn-primary" onClick={saveConfig} disabled={saving}>
          Guardar configuración
        </button>
      </section>

      <section className="disp-section">
        <h2>Horario semanal</h2>
        <p className="disp-hint">Define en qué días y franjas horarias aceptas reservas.</p>
        {[1, 2, 3, 4, 5, 6, 0].map((dia) => (
          <div key={dia} className="disp-day-block">
            <strong>{DIAS[dia]}</strong>
            {getSlotsByDay(dia).map((slot, i) => {
              const globalIdx = semanal.findIndex((s) => s === slot);
              return (
                <div key={globalIdx} className="disp-slot-row">
                  <input
                    type="time"
                    value={slot.hora_inicio || ''}
                    onChange={(e) => updateSlot(globalIdx, 'hora_inicio', e.target.value)}
                  />
                  <span>–</span>
                  <input
                    type="time"
                    value={slot.hora_fin || ''}
                    onChange={(e) => updateSlot(globalIdx, 'hora_fin', e.target.value)}
                  />
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => removeSlot(globalIdx)}>
                    Quitar
                  </button>
                </div>
              );
            })}
            <button type="button" className="btn btn-sm" onClick={() => addSlot(dia)}>
              + Añadir franja
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-primary" onClick={saveSemanal} disabled={saving}>
          Guardar horario semanal
        </button>
      </section>

      <section className="disp-section">
        <h2>Excepciones por fecha</h2>
        <p className="disp-hint">Bloquea días completos o franjas, o añade disponibilidad extra.</p>
        <div className="disp-excepcion-form">
          <input
            type="date"
            value={nuevaExcepcion.fecha}
            onChange={(e) => setNuevaExcepcion((n) => ({ ...n, fecha: e.target.value }))}
          />
          <select
            value={nuevaExcepcion.tipo}
            onChange={(e) => setNuevaExcepcion((n) => ({ ...n, tipo: e.target.value }))}
          >
            <option value="bloqueado">Bloquear</option>
            <option value="disponible">Añadir disponibilidad</option>
          </select>
          <input
            type="time"
            placeholder="Inicio"
            value={nuevaExcepcion.hora_inicio}
            onChange={(e) => setNuevaExcepcion((n) => ({ ...n, hora_inicio: e.target.value }))}
          />
          <input
            type="time"
            placeholder="Fin"
            value={nuevaExcepcion.hora_fin}
            onChange={(e) => setNuevaExcepcion((n) => ({ ...n, hora_fin: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Motivo (opcional)"
            value={nuevaExcepcion.motivo}
            onChange={(e) => setNuevaExcepcion((n) => ({ ...n, motivo: e.target.value }))}
          />
          <button type="button" className="btn btn-primary" onClick={addExcepcion} disabled={saving}>
            Añadir
          </button>
        </div>
        <ul className="disp-excepciones-list">
          {excepciones.map((ex) => (
            <li key={ex.id}>
              {ex.fecha} – {ex.tipo}
              {ex.hora_inicio && ` ${ex.hora_inicio}-${ex.hora_fin || ''}`}
              {ex.motivo && ` (${ex.motivo})`}
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => deleteExcepcion(ex.id)}>
                Eliminar
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="disp-section disp-preview">
        <h2>Enlace público</h2>
        <p>Comparte este enlace para que tus clientes reserven cita sin acceder al CRM:</p>
        {previewUrl ? (
          <>
            <div className="disp-preview-url">
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">{previewUrl}</a>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => navigator.clipboard?.writeText(previewUrl)}
              >
                Copiar
              </button>
            </div>
            <p style={{ fontSize: '13px', color: '#6c757d', marginTop: '8px' }}>
              Para la versión en inglés, añade <code>?lang=en</code> al enlace:
              <br />
              <a href={previewUrl + '?lang=en'} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
                {previewUrl}?lang=en
              </a>
            </p>
          </>
        ) : (
          <p className="disp-hint">Inicia sesión para ver tu enlace.</p>
        )}
      </section>
    </div>
  );
}

export default DisponibilidadConfig;
